
import Editable from './Editable';
import type { Translations, SeoData } from '../types';
import { assets } from '../assetsConfig';

interface AboutProps {
  t: Translations;
  seo: SeoData;
  isEditable: boolean;
  onUpdate: (key: string, val: string) => void;
}

export default function About({ t, seo, isEditable, onUpdate }: AboutProps) {
  return (
    <section id="ueber-uns" className="relative w-full bg-[#FAF8F5] pt-16 pb-24 lg:pt-24 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#c69c6d 1px, transparent 1px), radial-gradient(#c69c6d 1px, #FAF8F5 1px)', backgroundSize: '40px 40px', backgroundPosition: '0 0, 20px 20px' }}></div>

      <div className="container mx-auto px-4 mt-8 lg:mt-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32 items-center">
          
          <div className="relative w-full max-w-md mx-auto lg:mx-0 reveal-on-scroll">
            <div className="relative z-10 pt-4 md:pt-10">
              
              <div className="rounded-[2.5rem] overflow-hidden shadow-xl relative z-10 bg-white aspect-[4/5]">
                <img src={assets.img.pic2} alt={seo.aboutAlt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              
              <div className="absolute sm:bottom-20 sm:left-55 bottom-18 right-2 bg-white sm:p-5
               rounded-2xl shadow-2xl flex items-center gap-4 z-30 p-1">
                <div className="sm:w-12 sm:h-12 w-8 h-8 bg-[#c69c6d]/20 rounded-full flex items-center justify-center text-care">
                  <i className="ri-shield-star-line sm:text-2xl text-sm"></i>
                </div>
                <div className="w-full">
                  <Editable isEditable={isEditable} value={t.about_experience_number} onChange={(v) => onUpdate('about_experience_number', v)} as="p" className="font-extrabold sm:text-2xl text-sm text-prestige leading-none" />
                  <Editable isEditable={isEditable} value={t.about_experience} onChange={(v) => onUpdate('about_experience', v)} as="p" className="sm:text-sm text-xs font-bold text-slate-500 mt-1" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center mt-12 md:mt-0 reveal-on-scroll">
            <span className="text-care font-bold tracking-wider uppercase mb-3 flex items-center gap-2">
              <i className="ri-verified-badge-line text-xl"></i> 
              <Editable isEditable={isEditable} value={t.about_badge} onChange={(v) => onUpdate('about_badge', v)} as="span" />
            </span>
            
            <Editable isEditable={isEditable} value={t.about_heading} onChange={(v) => onUpdate('about_heading', v)} as="div" className="text-3xl sm:text-4xl lg:text-4xl font-extrabold text-prestige leading-[1.2] py-2" multiline />
            
            <div className="w-20 h-1.5 bg-care rounded-full mt-6 mb-8"></div>
            
            <Editable isEditable={isEditable} value={t.about_p1} onChange={(v) => onUpdate('about_p1', v)} as="div" className="text-base md:text-lg text-slate-600 leading-relaxed py-2" multiline />
            <Editable isEditable={isEditable} value={t.about_p2} onChange={(v) => onUpdate('about_p2', v)} as="div" className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed py-2" multiline />

            <ul className="mt-10 space-y-4">
              {['about_bullet1', 'about_bullet2', 'about_bullet3'].map((key, idx) => (
                <li key={idx} className="flex items-center gap-4 text-slate-700 font-bold text-[17px] group">
                  <div className="w-8 h-8 rounded-full bg-prestige flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform hover:shadow-lg">
                    <i className="ri-check-line"></i>
                  </div>
                  <Editable isEditable={isEditable} value={t[key]} onChange={(v) => onUpdate(key, v)} as="span" className="w-full" />
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>
    </section>
  );
}